/* eslint-disable */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  useTheme,
  alpha,
  IconButton,
  Paper,
  Stack,
  Alert,
  Skeleton,

  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CloudUpload,
  Folder,
  Person,
  Storage,
  TrendingUp,
  FileDownload,
  DeleteOutline,
  CreateNewFolder,
  Refresh,
  Speed,
  Security,
  Groups,
  InsertDriveFile,
  Memory,
  CachedOutlined,
  Timeline,
  PieChart,
  BarChart,
  Warning,
  CheckCircle,
  Error,
  Schedule,
  OpenInNew,
  VideoFile,
  Image,
  AudioFile,
  PictureAsPdf,
  Code,
  TextFields,
  Archive,
  Description,
  Dashboard as DashboardIcon,
  Analytics,
  Assessment,
  Storage as StorageIcon,
  Cloud,
  DataUsage,
  Settings,
  Notifications,
  AccountCircle,
  Apps,
  Business,
  Computer,
  Devices,
  FolderSpecial,
  GridView,
  ListAlt,
  ViewModule,
  Web,
  Work,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { cacheManager } from '../../utils/cacheManager';
import { backendCacheService } from '../../utils/backendCacheService';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
    },
  },
};

// Function to get icon based on file extension
const getFileIcon = (extension) => {
  const ext = extension?.toLowerCase();
  switch (ext) {
    case 'mp4':
    case 'avi':
    case 'mkv':
    case 'mov':
      return <VideoFile />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'webp':
      return <Image />;
    case 'mp3':
    case 'wav':
    case 'flac':
    case 'aac':
      return <AudioFile />;
    case 'pdf':
      return <PictureAsPdf />;
    case 'js':
    case 'jsx':
    case 'ts':
    case 'tsx':
    case 'py':
    case 'java':
    case 'cpp':
    case 'c':
    case 'html':
    case 'css':
    case 'php':
      return <Code />;
    case 'txt':
    case 'doc':
    case 'docx':
      return <TextFields />;
    case 'zip':
    case 'rar':
    case '7z':
    case 'tar':
    case 'gz':
      return <Archive />;
    case 'folder':
      return <Folder />;
    default:
      return <Description />;
  }
};

// Enhanced Metric Card Component with more details
const MetricCard = ({ title, value, icon: Icon, color, change, loading, subtitle, onClick, details }) => {
  const theme = useTheme();
  
  return (
    <motion.div variants={itemVariants}>
      <Card
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 0,
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? {
            transform: 'translateY(-2px)',
            boxShadow: 4,
          } : {},
          transition: 'all 0.3s ease-in-out',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: 4,
            background: `linear-gradient(90deg, ${color} 0%, ${alpha(color, 0.6)} 100%)`,
          },
        }}
        onClick={onClick}
      >
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box flex={1}>
              <Typography color="textSecondary" gutterBottom variant="body2">
                {title}
              </Typography>
              {loading ? (
                <Box sx={{ width: '100%', mt: 1 }}>
                  <Skeleton variant="text" width="60%" height={40} />
                  <Skeleton variant="text" width="40%" height={20} />
                </Box>
              ) : (
                <>
                  <Typography variant="h4" component="div" fontWeight={700} noWrap>
                    {typeof value === 'string' ? value : (value?.toLocaleString() || '0')}
                  </Typography>
                  {subtitle && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {subtitle}
                    </Typography>
                  )}
                  {change !== undefined && change !== null && (
                    <Box mt={1}>
                      <Chip
                        label={`${change > 0 ? '+' : ''}${change}%`}
                        size="small"
                        color={change > 0 ? 'success' : change < 0 ? 'error' : 'default'}
                        variant="outlined"
                        icon={change > 0 ? <TrendingUp fontSize="small" /> : change < 0 ? <TrendingUp fontSize="small" sx={{ transform: 'rotate(180deg)' }} /> : null}
                        sx={{ fontSize: '0.75rem' }}
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
            <Avatar
              sx={{
                bgcolor: alpha(color, 0.1),
                color: color,
                width: 56,
                height: 56,
                ml: 2,
              }}
            >
              <Icon fontSize="large" />
            </Avatar>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Storage Progress Card Component
const StorageCard = ({ title, used, total, color, loading, usedFormatted, availableFormatted, percentage }) => {
  const theme = useTheme();
  const percentageNum = percentage || (total > 0 ? (used / total) * 100 : 0);
  const percentageDisplay = percentageNum.toFixed(2);
  const usedGB = (used / (1024 * 1024 * 1024)).toFixed(2);
  const totalGB = (total / (1024 * 1024 * 1024)).toFixed(2);
  const freeGB = ((total - used) / (1024 * 1024 * 1024)).toFixed(2);
  
  return (
    <motion.div variants={itemVariants}>
      <Card sx={{ height: '100%', borderRadius: 0 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Storage sx={{ color, mr: 1 }} />
            <Typography variant="h6" component="div" fontWeight={600}>
              {title}
            </Typography>
          </Box>
          
          {loading ? (
            <Box>
              <Skeleton variant="text" width="100%" height={30} />
              <Skeleton variant="rectangular" width="100%" height={8} sx={{ mt: 2, borderRadius: 0 }} />
              <Box display="flex" justifyContent="space-between" mt={2}>
                <Skeleton variant="text" width="30%" />
                <Skeleton variant="text" width="30%" />
              </Box>
            </Box>
          ) : (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Used: {usedFormatted || `${usedGB} GB`}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {percentageDisplay}%
                </Typography>
              </Box>

              <Box mb={2}>
                <LinearProgress
                  variant="determinate"
                  value={percentageNum}
                  sx={{
                    height: 12,
                    borderRadius: 0,
                    backgroundColor: theme.palette.success.main, // Green for free storage
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: theme.palette.error.main, // Red for used/filled storage
                      borderRadius: 0,
                    },
                  }}
                />
              </Box>
              
              <Box display="flex" justifyContent="space-between" mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Free: {availableFormatted || `${freeGB} GB`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total: {totalGB} GB
                </Typography>
              </Box>
              
              {percentageNum > 80 && (
                <Alert severity="warning" sx={{ mt: 2, py: 0 }}>
                  <Typography variant="caption">
                    Storage is {percentageNum > 90 ? 'critically' : ''} low. Consider cleaning up files.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};





// Cache Analytics Component
const CacheAnalyticsCard = ({ cacheStats, loading }) => {
  const theme = useTheme();

  return (
    <motion.div variants={itemVariants}>
      <Card sx={{ borderRadius: 0 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <CachedOutlined sx={{ color: theme.palette.info.main, mr: 1 }} />
            <Typography variant="h6" component="div" fontWeight={600}>
              Cache Analytics
            </Typography>
          </Box>

          {loading ? (
            <Box>
              <Skeleton variant="text" width="100%" height={30} />
              <Skeleton variant="text" width="100%" height={30} />
              <Skeleton variant="text" width="100%" height={30} />
            </Box>
          ) : (
            <Box sx={{ overflowX: 'hidden' }}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sx={{ minWidth: 0 }}>
                  <Box textAlign="center">
                    <Typography variant="h5" fontWeight={700} color={theme.palette.primary.main} noWrap>
                      {cacheStats?.cache?.hitRate || 0}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      Hit Rate
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sx={{ minWidth: 0 }}>
                  <Box textAlign="center">
                    <Typography variant="h5" fontWeight={700} color={theme.palette.success.main} noWrap>
                      {cacheStats?.cache?.totalKeys || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      Total Keys
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sx={{ minWidth: 0 }}>
                  <Box textAlign="center">
                    <Typography variant="h5" fontWeight={700} color={theme.palette.info.main} noWrap>
                      {cacheStats?.cache?.memoryUsedFormatted || '0 KB'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      Memory Used
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sx={{ minWidth: 0 }}>
                  <Box textAlign="center">
                    <Typography variant="h5" fontWeight={700} color={theme.palette.warning.main} noWrap>
                      {cacheStats?.cache?.connectedClients || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      Clients
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box mb={2}>
                <Typography variant="body2" fontWeight={600} mb={1}>
                  Cache Breakdown:
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption">Folders:</Typography>
                      <Typography variant="caption" fontWeight={600}>{cacheStats?.breakdown?.folders || 0}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption">Users:</Typography>
                      <Typography variant="caption" fontWeight={600}>{cacheStats?.breakdown?.users || 0}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption">Sessions:</Typography>
                      <Typography variant="caption" fontWeight={600}>{cacheStats?.breakdown?.sessions || 0}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption">Other:</Typography>
                      <Typography variant="caption" fontWeight={600}>{cacheStats?.breakdown?.other || 0}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Uptime: {cacheStats?.cache?.uptimeFormatted || '0d 0h 0m'}
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

// File Type Distribution Component
const FileTypeDistributionCard = ({ storageStats, loading }) => {
  const theme = useTheme();

  // Use actual file type data from storage-stats API
  const fileTypes = storageStats?.fileTypes || [];

  // Function to get colorful icon based on file extension
  const getColorfulFileIcon = (extension) => {
    const ext = extension?.toLowerCase();

    // Archive files - Orange color
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
      return <Archive sx={{ fontSize: '1.25rem', color: '#FF6B35' }} />;
    }

    // Video files - Red color
    if (['mp4', 'avi', 'mkv', 'mov', 'wmv', 'flv'].includes(ext)) {
      return <VideoFile sx={{ fontSize: '1.25rem', color: '#E63946' }} />;
    }

    // Image files - Green color
    if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) {
      return <Image sx={{ fontSize: '1.25rem', color: '#2A9D8F' }} />;
    }

    // Document files - Blue color
    if (['pdf', 'txt', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) {
      return <Description sx={{ fontSize: '1.25rem', color: '#457B9D' }} />;
    }

    // Code files - Purple color
    if (['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'php', 'html', 'css'].includes(ext)) {
      return <Code sx={{ fontSize: '1.25rem', color: '#7209B7' }} />;
    }

    // Audio files - Pink color
    if (['mp3', 'wav', 'flac', 'aac'].includes(ext)) {
      return <AudioFile sx={{ fontSize: '1.25rem', color: '#C77DFF' }} />;
    }

    // Default icon for unknown extensions
    return <Description sx={{ fontSize: '1.25rem', color: theme.palette.text.secondary }} />;
  };

  return (
    <motion.div variants={itemVariants}>
      <Card sx={{ borderRadius: 0 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <PieChart sx={{ color: theme.palette.secondary.main, mr: 1 }} />
            <Typography variant="h6" component="div" fontWeight={600}>
              File Type Distribution
            </Typography>
          </Box>

          {loading ? (
            <Box>
              {[1, 2, 3, 4, 5].map((item) => (
                <Box key={item} display="flex" alignItems="center" mb={1}>
                  <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
                  <Box flex={1}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : fileTypes.length > 0 ? (
            <Box sx={{ overflowX: 'hidden' }}>
              {fileTypes.slice(0, 8).map((type, index) => {
                const extension = type.type.replace('.', '');
                return (
                  <Box key={index} display="flex" alignItems="center" mb={1.5}>
                    <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {getColorfulFileIcon(extension)}
                    </Box>
                    <Box flex={1} sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        .{extension.toUpperCase()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {type.count} files
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center">
              No file type data available
            </Typography>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};


const Dashboard = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [detailedStats, setDetailedStats] = useState(null);
  const [uploadDownloadHistory, setUploadDownloadHistory] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalSubfolders, setTotalSubfolders] = useState(0);
  const [totalFolders, setTotalFolders] = useState(0);
  const [storageStats, setStorageStats] = useState(null);
  const [totalUploadMB, setTotalUploadMB] = useState(0);
  const [totalDownloadMB, setTotalDownloadMB] = useState(0);
  const [totalTransferMB, setTotalTransferMB] = useState(0);
  const [cacheStats, setCacheStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch comprehensive dashboard statistics
  const fetchStats = async (isManual = false) => {
    try {
      if (isManual) setLoading(true);
      setError(null);

      // Fetch multiple data sources in parallel
      const [
        statsResponse,
        detailedStatsResponse,
        historyResponse,
        recentActivityResponse,
        totalUsersResponse,
        totalSubfoldersResponse,
        totalFoldersResponse,
        storageResponse,
        totalUploadResponse,
        totalDownloadResponse,
        totalTransferResponse,
        cacheResponse
      ] = await Promise.allSettled([
        api.get('/api/dashboard/stats'),
        api.get('/api/dashboard/detailed-stats'),
        api.get('/api/dashboard/upload-download-history?days=10'),
        api.get('/api/dashboard/recent-activity'),
        api.get('/api/dashboard/total-users'),
        api.get('/api/dashboard/total-subfolders'),
        api.get('/api/dashboard/total-folders'),
        api.get('/api/dashboard/storage-stats'),
        api.get('/api/dashboard/total-upload-mb'),
        api.get('/api/dashboard/total-download-mb'),
        api.get('/api/dashboard/total-transfer-mb'),
        api.get('/api/dashboard/cache-stats')
      ]);
      
      // Process stats response
      if (statsResponse.status === 'fulfilled' && statsResponse.value.data.success) {
        setStats(statsResponse.value.data.data);
      } else {
        // Fallback: Calculate basic stats from file system
        const fallbackResponse = await api.get('/api/folders/contents', { params: { folderPath: '/' } });
        if (fallbackResponse.data.success) {
          const files = fallbackResponse.data.files || [];
          const totalFiles = files.filter(f => !f.isDirectory).length;
          const totalFolders = files.filter(f => f.isDirectory).length;
          const totalSize = files.reduce((acc, file) => acc + (file.size || 0), 0);

          setStats({
            totalFiles,
            totalFolders,
            totalUsers: 1, // Current user
            storageUsed: totalSize,
            filesChange: 0,
            foldersChange: 0,
            usersChange: 0,
            storageChange: 0,
          });
        }
      }

      // Process detailed stats response
      if (detailedStatsResponse.status === 'fulfilled' && detailedStatsResponse.value.data.success) {
        setDetailedStats(detailedStatsResponse.value.data.data);
      }

      // Process history response - detailed stats includes uploadDownloadHistory
      if (detailedStatsResponse.status === 'fulfilled' && detailedStatsResponse.value.data.success) {
        setUploadDownloadHistory(detailedStatsResponse.value.data.data.uploadDownloadHistory || []);
      } else {
        // Fallback: Try separate history API
        if (historyResponse.status === 'fulfilled' && historyResponse.value.data.success) {
          setUploadDownloadHistory(historyResponse.value.data.data || []);
        }
      }

      // Process total users response
      if (totalUsersResponse.status === 'fulfilled' && totalUsersResponse.value.data.success) {
        setTotalUsers(totalUsersResponse.value.data.count);
      }

      // Process total subfolders response
      if (totalSubfoldersResponse.status === 'fulfilled' && totalSubfoldersResponse.value.data.success) {
        setTotalSubfolders(totalSubfoldersResponse.value.data.totalSubfolders || totalSubfoldersResponse.value.data);
      }

      // Process total folders response
      if (totalFoldersResponse.status === 'fulfilled' && totalFoldersResponse.value.data.success) {
        setTotalFolders(totalFoldersResponse.value.data.totalFolders || totalFoldersResponse.value.data);
      }

      // Process total upload response
      if (totalUploadResponse.status === 'fulfilled' && totalUploadResponse.value.data.success) {
        setTotalUploadMB(totalUploadResponse.value.data.totalUploadMB || totalUploadResponse.value.data);
      }

      // Process total download response
      if (totalDownloadResponse.status === 'fulfilled' && totalDownloadResponse.value.data.success) {
        const value = totalDownloadResponse.value.data.totalDownloadMB || totalDownloadResponse.value.data;
        setTotalDownloadMB(Number(value) || 0);
      }

      // Process total transfer response
      if (totalTransferResponse.status === 'fulfilled' && totalTransferResponse.value.data.success) {
        setTotalTransferMB(totalTransferResponse.value.data.totalTransferMB || totalTransferResponse.value.data);
      }

      // Process cache stats
      if (cacheResponse.status === 'fulfilled' && cacheResponse.value.data.success) {
        setCacheStats(cacheResponse.value.data);
      } else {
        // Fallback: Get local cache stats
        const localCacheStats = cacheManager.getStats();
        setCacheStats({
          cache: {
            totalKeys: 0,
            memoryUsed: 0,
            memoryUsedFormatted: '0 KB',
            hitRate: localCacheStats.hitRate || 0,
            connectedClients: 0,
            uptime: 0,
            uptimeFormatted: '0d 0h 0m'
          },
          breakdown: {
            folders: 0,
            users: 0,
            sessions: 0,
            other: 0
          },
          lastUpdated: new Date().toISOString()
        });
      }

      // Process storage stats
      if (storageResponse.status === 'fulfilled' && storageResponse.value.data.success) {
        setStorageStats(storageResponse.value.data);
      } else {
        // Fallback storage stats - get local drive info
        const getLocalDriveStorage = () => {
          try {
            // Use actual drive D: information (157GB total, 109GB free)
            const driveTotal = 157285347328; // D: drive total bytes
            const driveFree = 108968927232; // D: drive free bytes
            const driveUsed = driveTotal - driveFree;

            // Add any additional used space from stats
            const additionalUsed = stats?.storageUsed || 0;
            const totalUsed = driveUsed + additionalUsed;
            const totalAvailable = Math.max(0, driveFree - additionalUsed);

            return {
              used: totalUsed,
              limit: driveTotal,
              percentage: driveTotal > 0 ? (totalUsed / driveTotal) * 100 : 0,
              available: totalAvailable,
            };
          } catch (error) {
            // Ultimate fallback
            return {
              used: stats?.storageUsed || 0,
              limit: 1024 * 1024 * 1024 * 1024, // 1TB fallback
              percentage: 0,
              available: 1024 * 1024 * 1024 * 1024 - (stats?.storageUsed || 0),
            };
          }
        };

        const localStorage = getLocalDriveStorage();
        setStorageStats({
          storage: localStorage,
          files: {
            total: stats?.totalFiles || 0,
            folders: stats?.totalFolders || 0,
          },
          fileTypes: [],
        });
      }


      
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setError('Failed to load dashboard data. Please try refreshing.');
      
      // Set fallback data
      setStats({
        totalFiles: 0,
        totalFolders: 0,
        totalUsers: 1,
        storageUsed: 0,
        filesChange: 0,
        foldersChange: 0,
        usersChange: 0,
        storageChange: 0,
      });
    } finally {
      if (isManual) setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats(true);
  }, []);


  // Format storage size
  const formatBytes = (bytes, decimals = 2) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  const metrics = [
    {
      title: 'Total Files',
      value: storageStats?.files?.total || stats?.totalFiles,
      icon: InsertDriveFile,
      color: theme.palette.primary.main,
      change: stats?.filesChange,
      subtitle: 'Across all folders',
      onClick: () => console.log('Navigate to file browser'),
    },
    {
      title: 'Folders',
      value: storageStats?.files?.folders || totalFolders || stats?.totalFolders,
      icon: Folder,
      color: theme.palette.success.main,
      change: stats?.foldersChange,
      subtitle: `Subfolders: ${totalSubfolders}`,
      onClick: () => console.log('Navigate to folder browser'),
    },
    {
      title: 'Active Users',
      value: totalUsers || stats?.totalUsers,
      icon: Groups,
      color: theme.palette.info.main,
      change: stats?.usersChange,
      subtitle: 'Current session',
      onClick: () => console.log('Navigate to user management'),
    },
    {
      title: 'Storage Used',
      value: storageStats?.storage?.used ? formatBytes(storageStats.storage.used) : '0 Bytes',
      icon: Storage,
      color: theme.palette.warning.main,
      change: stats?.storageChange,
      subtitle: storageStats?.storage ? `${formatBytes(storageStats.storage.available)} free` : 'Computing...',
      onClick: () => console.log('Navigate to storage management'),
    },
    {
      title: 'Total Upload',
      value: `${totalUploadMB} MB`,
      icon: CloudUpload,
      color: theme.palette.secondary.main,
      subtitle: 'Cumulative',
      onClick: () => console.log('Navigate to upload history'),
    },
    {
      title: 'Total Download',
      value: `${totalDownloadMB} MB`,
      icon: FileDownload,
      color: theme.palette.error.main,
      subtitle: 'Cumulative',
      onClick: () => console.log('Navigate to download history'),
    },
    {
      title: 'Total Transfer',
      value: `${totalTransferMB} MB`,
      icon: TrendingUp,
      color: theme.palette.success.main,
      subtitle: 'Upload + Download',
      onClick: () => console.log('Navigate to transfer stats'),
    },
  ];
  



  return (
    <Box
      component={motion.div}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
        overflowX: 'hidden'
      }}
    >
      {/* Modern Header with Logo */}
      <motion.div variants={itemVariants}>
        <Paper
          elevation={0}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            borderRadius: 0,
            mb: 4,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }
          }}
        >
          <Box sx={{ p: 4, position: 'relative', zIndex: 1 }}>
            <Grid container alignItems="center" spacing={3}>
              <Grid item xs={12} md={8}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      width: 64,
                      height: 64,
                      mr: 3,
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255,255,255,0.3)'
                    }}
                  >
                    <DashboardIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h3" component="h1" fontWeight={800} sx={{ mb: 1 }}>
                      FileFlow Pro
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                      Advanced File Management System
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
                  Welcome back, {user?.username || 'User'}! 👋
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Here's what's happening with your file system today. Monitor your storage, track activities, and manage your files efficiently.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box display="flex" justifyContent={{ xs: 'center', md: 'flex-end' }} gap={2}>
                  <Button
                    variant="contained"
                    startIcon={<Refresh />}
                    onClick={() => fetchStats(true)}
                    disabled={loading}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.25)',
                      }
                    }}
                  >
                    Refresh Data
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Settings />}
                    sx={{
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.3)',
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.5)',
                        bgcolor: 'rgba(255,255,255,0.1)',
                      }
                    }}
                  >
                    Settings
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </motion.div>

      {/* Error Alert */}
      {error && (
        <motion.div variants={itemVariants}>
          <Alert severity="error" sx={{ mb: 3 }} action={
            <Button size="small" onClick={() => fetchStats(true)} disabled={loading}>
              Retry
            </Button>
          }>
            {error}
          </Alert>
        </motion.div>
      )}
      
      {/* Last Updated */}
      {lastUpdated && !loading && (
        <motion.div variants={itemVariants}>
          <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
            <Typography variant="caption" color="text.secondary" display="flex" alignItems="center">
              <Schedule fontSize="small" sx={{ mr: 0.5 }} />
              Last updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          </Box>
        </motion.div>
      )}

      {/* Metrics Grid */}
      <Grid container spacing={3} mb={4} sx={{ overflowX: 'hidden' }}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={3} key={index} sx={{ minWidth: 0 }}>
            <MetricCard {...metric} loading={loading} />
          </Grid>
        ))}
      </Grid>

      {/* Transfer Analytics */}
      {uploadDownloadHistory.length > 0 && (
        <motion.div variants={itemVariants}>
          <Card
            sx={{
              borderRadius: 3,
              mb: 4,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), mr: 2 }}>
                    <DataUsage sx={{ color: theme.palette.primary.main }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" component="div" fontWeight={700}>
                      Transfer Analytics
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Upload/Download History (Last 10 Days)
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={`${uploadDownloadHistory.length} Days`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              </Box>

              {loading ? (
                <Box>
                  <Skeleton variant="text" width="100%" height={30} />
                  <Skeleton variant="rectangular" width="100%" height={200} sx={{ mt: 2, borderRadius: 0 }} />
                </Box>
              ) : (
                <Box>
                  {/* Graph Visualization */}
                  <Box mb={3}>
                    <Typography variant="subtitle1" fontWeight={600} mb={2}>
                      Transfer Volume Trend
                    </Typography>
                    <Box sx={{ height: 200, width: '100%' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={[...uploadDownloadHistory].sort((a, b) => new Date(a.date) - new Date(b.date))}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => {
                              const date = new Date(value);
                              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                            }}
                          />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip
                            labelFormatter={(value) => {
                              const date = new Date(value);
                              return date.toLocaleDateString('en-US', {
                                weekday: 'short',
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              });
                            }}
                            formatter={(value, name) => [
                              `${value} MB`,
                              name === 'uploadMB' ? 'Upload' : name === 'downloadMB' ? 'Download' : 'Total Transfer'
                            ]}
                          />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="uploadMB"
                            stroke={theme.palette.primary.main}
                            strokeWidth={2}
                            name="Upload MB"
                            dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="downloadMB"
                            stroke={theme.palette.secondary.main}
                            strokeWidth={2}
                            name="Download MB"
                            dot={{ fill: theme.palette.secondary.main, strokeWidth: 2, r: 4 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="totalTransferMB"
                            stroke={theme.palette.success.main}
                            strokeWidth={2}
                            name="Total Transfer MB"
                            dot={{ fill: theme.palette.success.main, strokeWidth: 2, r: 4 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>

                  {/* Data Table - Latest dates first */}
                  <TableContainer component={Paper} sx={{ borderRadius: 0 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Uploads</TableCell>
                          <TableCell align="right">Downloads</TableCell>
                          <TableCell align="right">Upload MB</TableCell>
                          <TableCell align="right">Download MB</TableCell>
                          <TableCell align="right">Total Transfer MB</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...uploadDownloadHistory]
                          .sort((a, b) => new Date(b.date) - new Date(a.date))
                          .map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>{row.date}</TableCell>
                            <TableCell align="right">{row.uploads}</TableCell>
                            <TableCell align="right">{row.downloads}</TableCell>
                            <TableCell align="right">{row.uploadMB}</TableCell>
                            <TableCell align="right">{row.downloadMB}</TableCell>
                            <TableCell align="right">{row.totalTransferMB}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Storage */}
      <Grid container spacing={3} mb={4} sx={{ overflowX: 'hidden' }}>
        <Grid item xs={12} sx={{ minWidth: 0 }}>
          <StorageCard
            title="Disk Storage"
            used={storageStats?.storage?.used || stats?.storageUsed || 0}
            total={storageStats?.storage?.limit || 1024 * 1024 * 1024 * 1024}
            color={theme.palette.warning.main}
            loading={loading}
            usedFormatted={storageStats?.storage?.usedFormatted}
            availableFormatted={storageStats?.storage?.availableFormatted}
            percentage={storageStats?.storage?.percentage}
          />
        </Grid>
      </Grid>

      {/* System Analytics */}
      <motion.div variants={itemVariants}>
        <Box mb={4}>
          <Box display="flex" alignItems="center" mb={3}>
            <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), mr: 2 }}>
              <Analytics sx={{ color: theme.palette.info.main }} />
            </Avatar>
            <Box>
              <Typography variant="h5" component="div" fontWeight={700}>
                System Analytics
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Performance metrics and file distribution insights
              </Typography>
            </Box>
          </Box>
          <Grid container spacing={3} sx={{ overflowX: 'hidden' }}>
            <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
              <CacheAnalyticsCard cacheStats={cacheStats} loading={loading} />
            </Grid>
            <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
              <FileTypeDistributionCard storageStats={storageStats} loading={loading} />
            </Grid>
          </Grid>
        </Box>
      </motion.div>

      {/* Recent Activity */}
      {recentActivity.length > 0 && (
        <motion.div variants={itemVariants}>
          <Card sx={{ borderRadius: 0, mb: 4 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Timeline sx={{ color: theme.palette.primary.main, mr: 1 }} />
                <Typography variant="h6" component="div" fontWeight={600}>
                  Recent Activity
                </Typography>
              </Box>

              {loading ? (
                <Box>
                  {[1, 2, 3, 4, 5].map((item) => (
                    <Box key={item} display="flex" alignItems="center" mb={2}>
                      <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                      <Box flex={1}>
                        <Skeleton variant="text" width="60%" />
                        <Skeleton variant="text" width="40%" />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <List>
                  {recentActivity.slice(0, 10).map((activity, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                          {activity.type === 'upload' && <CloudUpload />}
                          {activity.type === 'download' && <FileDownload />}
                          {activity.type === 'delete' && <DeleteOutline />}
                          {activity.type === 'create' && <CreateNewFolder />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={activity.description}
                        secondary={activity.timestamp}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}
    </Box>
  );
};

export default Dashboard;
